import type { ReactNode } from 'react';
import { useEffect } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

interface LegalPageProps {
  title: string;
  intro?: string;
  children: ReactNode;
}

/** Shared layout for the public legal / trust prose pages. */
export default function LegalPage({ title, intro, children }: LegalPageProps) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} — My Bets`;
    // Restore the previous title on unmount so navigating away (e.g. back to the
    // home page, which does not manage the title) doesn't leave a stale tab title.
    return () => {
      document.title = previous;
    };
  }, [title]);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background">
      <Header />
      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl font-bold text-text-primary sm:text-4xl">
            {title}
          </h1>
          {intro && (
            <p className="mt-3 text-base leading-relaxed text-text-secondary">{intro}</p>
          )}
          <div className="legal-prose mt-8 space-y-8 text-text-secondary">
            {children}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/** A titled section with consistent spacing and heading styles. */
export function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="font-heading text-xl font-bold text-text-primary">{heading}</h2>
      <div className="space-y-3 text-sm leading-relaxed sm:text-base">{children}</div>
    </section>
  );
}
